import { useState } from 'react';
import { useUsers } from '@/hooks/useUsers';
import {
  useUserManagementDetails,
  useLdapGroups,
  useDeactivateUser,
  useActivateUser,
  useChangePassword,
  useResetPassword,
  useAddUserToGroup,
  useRemoveUserFromGroup,
  LdapGroup,
} from '@/hooks/useUserManagement';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  UserCog,
  Search,
  CheckCircle,
  XCircle,
  Shield,
  User as UserIcon,
  Mail,
  X,
  Key,
  KeyRound,
  Users as UsersIcon,
  UserMinus,
  UserPlus,
  AlertTriangle,
  Trash2,
  Plus,
} from 'lucide-react';
import { User, UserRole } from '@/types';
import { EmailField } from '@/components/users/EmailField';

/**
 * Role badge component
 */
function RoleBadge({ role }: { role: UserRole }) {
  const roleConfig: Record<UserRole, { label: string; className: string }> = {
    executive: { label: 'Executive', className: 'bg-purple-100 text-purple-800' },
    pm: { label: 'PM', className: 'bg-blue-100 text-blue-800' },
    developer: { label: 'Developer', className: 'bg-green-100 text-green-800' },
    support: { label: 'Support', className: 'bg-orange-100 text-orange-800' },
    viewer: { label: 'Viewer', className: 'bg-gray-100 text-gray-800' },
  };

  const config = roleConfig[role];

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.className}`}>
      <Shield className="h-3 w-3 mr-1" />
      {config.label}
    </span>
  );
}

/**
 * Status badge component
 */
function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center text-xs text-green-600">
      <CheckCircle className="h-3 w-3 mr-1" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center text-xs text-red-600">
      <XCircle className="h-3 w-3 mr-1" />
      Inactive
    </span>
  );
}

/**
 * Confirmation modal
 */
function ConfirmModal({
  title,
  message,
  confirmLabel,
  variant = 'destructive',
  isLoading,
  onConfirm,
  onClose,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  variant?: 'destructive' | 'default';
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-full ${variant === 'destructive' ? 'bg-red-100' : 'bg-blue-100'}`}>
            <AlertTriangle className={`h-5 w-5 ${variant === 'destructive' ? 'text-red-600' : 'text-blue-600'}`} />
          </div>
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
        <p className="text-muted-foreground mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant={variant} onClick={onConfirm} disabled={isLoading}>
            {isLoading ? <LoadingSpinner className="h-4 w-4 mr-2" /> : null}
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Change password modal
 */
function ChangePasswordModal({
  userId,
  userName,
  onClose,
}: {
  userId: string;
  userName: string;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const changePasswordMutation = useChangePassword();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({ userId, newPassword: password });
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to change password');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Change Password</h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-muted-foreground mb-4">
          Set a new LDAP password for <strong>{userName}</strong>
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password">New Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              minLength={8}
              required
            />
          </div>
          <div>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
            />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={changePasswordMutation.isPending}>
              {changePasswordMutation.isPending ? <LoadingSpinner className="h-4 w-4 mr-2" /> : null}
              Change Password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Add to group modal
 */
function AddToGroupModal({
  userId,
  userName,
  currentGroups,
  onClose,
}: {
  userId: string;
  userName: string;
  currentGroups: LdapGroup[];
  onClose: () => void;
}) {
  const [selectedGroup, setSelectedGroup] = useState('');
  const { data: allGroups, isLoading: groupsLoading } = useLdapGroups();
  const addToGroupMutation = useAddUserToGroup();

  // Filter out groups user is already in
  const currentGroupDns = new Set(currentGroups.map((g) => g.dn.toLowerCase()));
  const availableGroups = allGroups?.filter((g) => !currentGroupDns.has(g.dn.toLowerCase())) || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroup) return;

    try {
      await addToGroupMutation.mutateAsync({ userId, groupDn: selectedGroup });
      onClose();
    } catch (err) {
      console.error('Failed to add to group:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Add to Group</h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-muted-foreground mb-4">
          Add <strong>{userName}</strong> to an LDAP group
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="group">Select Group</Label>
            {groupsLoading ? (
              <div className="flex items-center gap-2 py-2">
                <LoadingSpinner className="h-4 w-4" />
                <span className="text-sm text-muted-foreground">Loading groups...</span>
              </div>
            ) : availableGroups.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                User is already a member of all available groups
              </p>
            ) : (
              <select
                id="group"
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                required
              >
                <option value="">Select a group...</option>
                {availableGroups.map((group) => (
                  <option key={group.dn} value={group.dn}>
                    {group.name} {group.description ? `- ${group.description}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          {addToGroupMutation.isError && (
            <p className="text-sm text-red-500">Failed to add user to group</p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!selectedGroup || addToGroupMutation.isPending || availableGroups.length === 0}
            >
              {addToGroupMutation.isPending ? <LoadingSpinner className="h-4 w-4 mr-2" /> : null}
              Add to Group
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * User management detail panel
 */
function UserManagementDetailPanel({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}) {
  const [modal, setModal] = useState<
    'deactivate' | 'activate' | 'changePassword' | 'resetPassword' | 'addGroup' | 'removeGroup' | null
  >(null);
  const [groupToRemove, setGroupToRemove] = useState<LdapGroup | null>(null);

  const { data: details, isLoading, refetch } = useUserManagementDetails(user.id);
  const deactivateMutation = useDeactivateUser();
  const activateMutation = useActivateUser();
  const resetPasswordMutation = useResetPassword();
  const removeFromGroupMutation = useRemoveUserFromGroup();

  const handleDeactivate = async () => {
    await deactivateMutation.mutateAsync(user.id);
    setModal(null);
    refetch();
  };

  const handleActivate = async () => {
    await activateMutation.mutateAsync(user.id);
    setModal(null);
    refetch();
  };

  const handleResetPassword = async () => {
    await resetPasswordMutation.mutateAsync(user.id);
    setModal(null);
  };

  const handleRemoveFromGroup = async () => {
    if (!groupToRemove) return;
    await removeFromGroupMutation.mutateAsync({ userId: user.id, groupDn: groupToRemove.dn });
    setModal(null);
    setGroupToRemove(null);
    refetch();
  };

  const currentUser = details?.user || user;
  const ldapGroups = details?.ldapGroups || [];
  const manager = details?.manager;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end">
      <div className="w-full max-w-lg bg-background h-full flex flex-col shadow-xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <UserIcon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-medium">{currentUser.displayName}</h3>
              <p className="text-sm text-muted-foreground">@{currentUser.username}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* User Info */}
          <div className="p-4 border-b bg-muted/50">
            <div className="flex flex-wrap gap-4 text-sm">
              <EmailField userId={currentUser.id} email={currentUser.email} canEdit />
              <RoleBadge role={currentUser.role} />
              <StatusBadge active={currentUser.active} />
            </div>
            {!currentUser.ldapDn && (
              <div className="mt-2 text-sm text-orange-600">
                User is not linked to LDAP - management features limited
              </div>
            )}
          </div>

          {/* Manager */}
          <div className="p-4 border-b">
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <UserCog className="h-4 w-4" />
              Manager
            </h4>
            {isLoading ? (
              <div className="flex items-center gap-2">
                <LoadingSpinner className="h-4 w-4" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : manager ? (
              <div className="flex items-center gap-3 p-2 bg-muted rounded">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <UserIcon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm">{manager.displayName}</p>
                  <p className="text-xs text-muted-foreground">@{manager.username}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No manager assigned</p>
            )}
          </div>

          {/* Groups */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <UsersIcon className="h-4 w-4" />
                LDAP Groups ({ldapGroups.length})
              </h4>
              {currentUser.ldapDn && (
                <Button variant="outline" size="sm" onClick={() => setModal('addGroup')}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              )}
            </div>
            {isLoading ? (
              <div className="flex items-center gap-2">
                <LoadingSpinner className="h-4 w-4" />
                <span className="text-sm text-muted-foreground">Loading groups...</span>
              </div>
            ) : ldapGroups.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not a member of any groups</p>
            ) : (
              <div className="space-y-2">
                {ldapGroups.map((group) => (
                  <div
                    key={group.dn}
                    className="flex items-center justify-between p-2 bg-muted rounded text-sm"
                  >
                    <div>
                      <p className="font-medium">{group.name}</p>
                      {group.description && (
                        <p className="text-xs text-muted-foreground">{group.description}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => {
                        setGroupToRemove(group);
                        setModal('removeGroup');
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-4">
            <h4 className="text-sm font-medium mb-3">Management Actions</h4>
            <div className="grid grid-cols-2 gap-3">
              {currentUser.active ? (
                <Button
                  variant="outline"
                  className="justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setModal('deactivate')}
                  disabled={!currentUser.ldapDn}
                >
                  <UserMinus className="h-4 w-4 mr-2" />
                  Deactivate
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="justify-start text-green-600 hover:text-green-700 hover:bg-green-50"
                  onClick={() => setModal('activate')}
                  disabled={!currentUser.ldapDn}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Activate
                </Button>
              )}
              <Button
                variant="outline"
                className="justify-start"
                onClick={() => setModal('changePassword')}
                disabled={!currentUser.ldapDn}
              >
                <Key className="h-4 w-4 mr-2" />
                Change Password
              </Button>
              <Button
                variant="outline"
                className="justify-start col-span-2"
                onClick={() => setModal('resetPassword')}
                disabled={!currentUser.ldapDn || !currentUser.email}
              >
                <KeyRound className="h-4 w-4 mr-2" />
                Reset Password & Email
              </Button>
            </div>
            {!currentUser.ldapDn && (
              <p className="text-xs text-muted-foreground mt-3">
                LDAP actions are disabled because this user is not linked to LDAP
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {modal === 'deactivate' && (
        <ConfirmModal
          title="Deactivate User"
          message={`Are you sure you want to deactivate ${currentUser.displayName}? This will disable their account in both LDAP and the system.`}
          confirmLabel="Deactivate"
          variant="destructive"
          isLoading={deactivateMutation.isPending}
          onConfirm={handleDeactivate}
          onClose={() => setModal(null)}
        />
      )}

      {modal === 'activate' && (
        <ConfirmModal
          title="Activate User"
          message={`Are you sure you want to activate ${currentUser.displayName}? This will enable their account in both LDAP and the system.`}
          confirmLabel="Activate"
          variant="default"
          isLoading={activateMutation.isPending}
          onConfirm={handleActivate}
          onClose={() => setModal(null)}
        />
      )}

      {modal === 'changePassword' && (
        <ChangePasswordModal
          userId={user.id}
          userName={currentUser.displayName}
          onClose={() => setModal(null)}
        />
      )}

      {modal === 'resetPassword' && (
        <ConfirmModal
          title="Reset Password"
          message={`This will generate a new random password for ${currentUser.displayName} and send it to their email (${currentUser.email}). Continue?`}
          confirmLabel="Reset & Email"
          variant="default"
          isLoading={resetPasswordMutation.isPending}
          onConfirm={handleResetPassword}
          onClose={() => setModal(null)}
        />
      )}

      {modal === 'addGroup' && (
        <AddToGroupModal
          userId={user.id}
          userName={currentUser.displayName}
          currentGroups={ldapGroups}
          onClose={() => {
            setModal(null);
            refetch();
          }}
        />
      )}

      {modal === 'removeGroup' && groupToRemove && (
        <ConfirmModal
          title="Remove from Group"
          message={`Are you sure you want to remove ${currentUser.displayName} from the group "${groupToRemove.name}"?`}
          confirmLabel="Remove"
          variant="destructive"
          isLoading={removeFromGroupMutation.isPending}
          onConfirm={handleRemoveFromGroup}
          onClose={() => {
            setModal(null);
            setGroupToRemove(null);
          }}
        />
      )}

      {/* Reset Password Success */}
      {resetPasswordMutation.isSuccess && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Password Reset Successful</h3>
            </div>
            <p className="text-muted-foreground mb-2">
              A new password has been generated and{' '}
              {resetPasswordMutation.data?.emailSent ? (
                <span className="text-green-600 font-medium">sent to the user's email</span>
              ) : (
                <span className="text-orange-600 font-medium">
                  email delivery failed - please notify the user manually
                </span>
              )}
              .
            </p>
            <div className="flex justify-end mt-4">
              <Button onClick={() => resetPasswordMutation.reset()}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * User card for management
 */
function UserManagementCard({ user, onClick }: { user: User; onClick: () => void }) {
  return (
    <Card
      className="hover:border-primary/50 transition-colors cursor-pointer"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <UserIcon className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium truncate">{user.displayName}</span>
              <RoleBadge role={user.role} />
              <StatusBadge active={user.active} />
            </div>
            <p className="text-sm text-muted-foreground">@{user.username}</p>
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <Mail className="h-3 w-3" />
              {user.email}
            </div>
            {!user.ldapDn && (
              <span className="inline-block mt-2 text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded">
                Not linked to LDAP
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            <UserCog className="h-5 w-5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function UserManagement() {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { data: usersData, isLoading } = useUsers({
    page,
    limit: 20,
    search: searchQuery || undefined,
    role: roleFilter || undefined,
    active: activeFilter === 'all' ? undefined : activeFilter === 'active',
    sortBy: 'displayName',
    sortOrder: 'asc',
  });

  const totalUsers = usersData?.pagination?.total || 0;
  const activeCount = usersData?.data?.filter((u) => u.active).length || 0;
  const inactiveCount = usersData?.data?.filter((u) => !u.active).length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
        <p className="text-muted-foreground">
          Manage user accounts, passwords, and group memberships
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsers}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{activeCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Inactive</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{inactiveCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value as UserRole | '');
            setPage(1);
          }}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All roles</option>
          <option value="executive">Executive</option>
          <option value="pm">PM</option>
          <option value="developer">Developer</option>
          <option value="support">Support</option>
          <option value="viewer">Viewer</option>
        </select>
        <select
          value={activeFilter}
          onChange={(e) => {
            setActiveFilter(e.target.value as 'all' | 'active' | 'inactive');
            setPage(1);
          }}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="all">All status</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      {/* User List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : !usersData?.data?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UserCog className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No users found</h3>
            <p className="text-muted-foreground">
              {searchQuery || roleFilter || activeFilter !== 'all'
                ? 'Try different filters'
                : 'No users in the system'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {usersData.data.map((user) => (
            <UserManagementCard
              key={user.id}
              user={user}
              onClick={() => setSelectedUser(user)}
            />
          ))}
        </div>
      )}

      {/* Detail Panel */}
      {selectedUser && (
        <UserManagementDetailPanel
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}

      {/* Pagination */}
      {usersData?.pagination?.totalPages && usersData.pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} of {usersData.pagination.totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page >= usersData.pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
